import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { downloadFile, FilterLayout, FilterProp, formatRelativeTimeForTable, InventoryParamChangeTypeBadge, renderHeaderActionButtons, SidePopover, TableDashboard, useAppDispatch, useAppSelector } from '../../globalUtils/globalExports';
import { useNavigate } from 'react-router-dom';
import { exportInventoryParamChangeLogReport, exportInventoryParamHistoryReport, exportInventoryParamReport, fetchChargerVisibleIdsPaginatedList, fetchInventoryParamChangeLogsReport, fetchInventoryParamHistoryReport, fetchInventoryParamLatestLogsReport, getCurrentFiltersValues, getCurrentScreenAppliedFiltersCount, getFilterReducer, getFilterSelectedValue, getReferenceDataReducer, getReportReducer, setFilterData, setInventoryParamChangeLogsReportTableProps, setInventoryParamHistoryReportTableProps, setInventoryParamLatestLogsReportTableProps } from '../../rmsReduxStore/reduxExports';
import { cloneDeep } from 'lodash';
import { Id, toast } from 'react-toastify';
import { Menu, MenuItem } from '@mui/material';
import './inventoryParamReportDashboard.css';


/*eslint-disable @typescript-eslint/no-unused-vars*/

function InventoryParamReportDashboard() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [selectedNav, setSelectedNav] = useState<'latest' | 'history' | 'changeLogs'>('history');
    const navList = [
        {
            id: 'history',
            text: 'Inventory Logs'
        },
        {
            id: 'latest',
            text: 'Latest Inventory Logs'
        },
        {
            id:'changeLogs',
            text:'Inventory Change History'
        }
    ];

    const [filtersVisible, setFiltersVisible] = useState(false);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [exportingFile, setExportingFile] = useState(false);
    const { inventoryParamHistoryData, inventoryParamHistoryTableProps, inventoryParamHistoryLoader, inventoryParamLatestLogsData, inventoryParamLatestLogsDataCount, inventoryParamLatestLogsTableProps, inventoryParamChangeLogsData, inventoryParamChangeLogsTableProps, inventoryParamHistoryColumns, inventoryParamLatestLogsColumns, inventoryParamChangeLogsColumns } = useAppSelector(getReportReducer);
    const { chargerVisibleIdReferenceData, chargerProductTypeReferenceData, inventoryParamComponentTypeReferenceData, inventoryParamChangeLogFieldNameReferenceData, inventoryParamChangeLogChangeTypeReferenceData } = useAppSelector(getReferenceDataReducer);
    const { screenFilters, globalFilters } = useAppSelector(getFilterReducer);
    const inventoryParamHistoryTablePropsRef = useRef(inventoryParamHistoryTableProps);
    const inventoryParamLatestLogsTablePropsRef = useRef(inventoryParamLatestLogsTableProps);
    const inventoryParamChangeLogsTablePropsRef = useRef(inventoryParamChangeLogsTableProps);

    /* Prefix for every dom id owned by the selected nav. */
    const navIdPrefix = useMemo(() => {
        switch (selectedNav) {
            case 'latest': return 'inventory-param-latest-logs-report';
            case 'history': return 'inventory-param-history-report';
            case 'changeLogs': return 'inventory-param-change-logs-report';
            default: return 'inventory-param-history-report';
        }
    }, [selectedNav]);

    const handleNavClick = async (nav) => {
        let tableProps: IListTableProps | null = null;
        switch(nav) {
            case 'latest':
                tableProps = cloneDeep(inventoryParamLatestLogsTablePropsRef?.current);
                if(tableProps) {
                    tableProps.searchTerm = '';
                }
                await dispatch(setInventoryParamLatestLogsReportTableProps(tableProps));
                break;
            case 'history':
                tableProps = cloneDeep(inventoryParamHistoryTablePropsRef?.current);
                if(tableProps) {
                    tableProps.searchTerm = '';
                }
                await dispatch(setInventoryParamHistoryReportTableProps(tableProps));
                break;
            case 'changeLogs':
                tableProps = cloneDeep(inventoryParamChangeLogsTablePropsRef?.current);
                if(tableProps) {
                    tableProps.searchTerm = '';
                }
                await dispatch(setInventoryParamChangeLogsReportTableProps(tableProps));
                break;
            default:
                tableProps = cloneDeep(inventoryParamHistoryTablePropsRef?.current);
                if(tableProps) {
                    tableProps.searchTerm = '';
                }
                await dispatch(setInventoryParamHistoryReportTableProps(tableProps));
        }
        setSelectedNav(nav);
    }

    const navBarconfigurations = {
        selectedNav: selectedNav,
        navList: navList,
        onNavClick: handleNavClick
    }

    useEffect(() => {
        inventoryParamHistoryTablePropsRef.current = inventoryParamHistoryTableProps
    }, [inventoryParamHistoryTableProps]);

    useEffect(() => {
        inventoryParamLatestLogsTablePropsRef.current = inventoryParamLatestLogsTableProps
    }, [inventoryParamLatestLogsTableProps]);

    useEffect(() => {
        inventoryParamChangeLogsTablePropsRef.current = inventoryParamChangeLogsTableProps
    }, [inventoryParamChangeLogsTableProps]);

    const activeFilterScreen = useMemo(()=> {
        switch (selectedNav) {
            case 'latest':  return 'inventoryParamLatestLogsReport';
            case 'history': return 'inventoryParamHistoryReport';
            case 'changeLogs': return 'inventoryParamChangeLogsReport';
            default: return 'inventoryParamHistoryReport';
        }
    },[selectedNav]);

    const activeScreenTablePropsRef = useMemo(()=> {
        switch (selectedNav) {
            case 'latest':  return inventoryParamLatestLogsTablePropsRef;
            case 'history': return inventoryParamHistoryTablePropsRef;
            case 'changeLogs': return inventoryParamChangeLogsTablePropsRef;
            default: return inventoryParamHistoryTablePropsRef;
        }
    },[selectedNav]);

    const fetchInventoryReportData = useMemo(()=>{
        switch (selectedNav) {
            case 'latest':  return fetchInventoryParamLatestLogsReport;
            case 'history': return fetchInventoryParamHistoryReport;
            case 'changeLogs': return fetchInventoryParamChangeLogsReport;
            default: return fetchInventoryParamHistoryReport;
        }
    },[selectedNav]);

    const setInventoryReportTableProps = useMemo(()=>{
        switch (selectedNav) {
            case 'latest':  return setInventoryParamLatestLogsReportTableProps;
            case 'history': return setInventoryParamHistoryReportTableProps;
            case 'changeLogs': return setInventoryParamChangeLogsReportTableProps;
            default: return fetchInventoryParamHistoryReport;
        }
    },[selectedNav])

    const layoutCallback = useCallback(
        async (
            pageNumber: number,
            pageSize: number,
            view: string,
            field: string | null,
            order: string,
            filterData,
            searchTerm: string,
        ) => {
            const tableProps = cloneDeep(activeScreenTablePropsRef?.current);
            const inventoryParamLogsFilter = getCurrentFiltersValues(activeFilterScreen);
            const appliedFilters = filterData ? filterData : inventoryParamLogsFilter;

            await dispatch(
                fetchInventoryReportData({
                    view: view ?? 'list',
                    sortBy: field || tableProps?.sortBy,
                    order: order || tableProps?.order,
                    pageSize: pageSize,
                    pageNumber: pageNumber,
                    filters: appliedFilters,
                    searchTerm: searchTerm != null ? searchTerm : tableProps?.searchTerm,
                })
            );

            if (tableProps) {
                tableProps.view = view || 'list'
                tableProps.sortBy = field || activeScreenTablePropsRef?.current?.sortBy;
                tableProps.order = order || activeScreenTablePropsRef?.current?.order;
                tableProps.pageNumber = pageNumber;
                tableProps.pageSize = pageSize;
                if (searchTerm != null && searchTerm != undefined) {
                    tableProps.searchTerm = searchTerm;
                }
            }
            await dispatch(setInventoryReportTableProps(tableProps));
        },
        [activeScreenTablePropsRef, activeFilterScreen, setInventoryReportTableProps, fetchInventoryReportData, dispatch]
    );

    const headerActionBtns = useMemo(() => renderHeaderActionButtons({
        actionBtns: [
            {
                id: 'add_filter_btn',
                isAccessible: true,
                headerActionBtnClick: (): void => {
                    setFiltersVisible(true);
                },
                badgeCount: getCurrentScreenAppliedFiltersCount(activeFilterScreen),
            },
            {
                id: 'export_csv_btn',
                isAccessible: true,
                headerActionBtnClick: (e): void => {
                    setExportAnchorEl(e.currentTarget)
                },
            },
        ]
    }),[activeFilterScreen, screenFilters, globalFilters]);

    const handleFiltersClosePopOver = (): void => {
        setFiltersVisible(false);
    }

    const handleClose = (): void => {
        setExportAnchorEl(null);
    };

    const fileDownloadSuccessToast = useCallback((): Id => {
        return toast.success('File downloaded.');
    }, []);

    const fileDownloadFailureToast = useCallback((): Id => {
        return toast.warn('Something went wrong. Please export again.');
    }, []);

    const onResetButtonClick = useCallback(async (callList: boolean) => {
        const res = await setFilterData(null, '', activeFilterScreen, 'CLEAR', true)(dispatch);
        if (res?.message === 'Action dispatched successfully') {
            // Reset the filters
            if (callList && activeScreenTablePropsRef?.current) {
                const tableProps = cloneDeep(activeScreenTablePropsRef?.current);
                await layoutCallback(
                    1,
                    tableProps?.pageSize,
                    tableProps?.view,
                    tableProps?.sortBy,
                    tableProps?.order,
                    res?.filters,
                    tableProps?.searchTerm ?? '',
                )
            }
        }
    
    }, [activeFilterScreen, activeScreenTablePropsRef, layoutCallback]);


    const filterFooterBtns = useMemo(() => {
        return [
            {
                buttonText: 'Reset',
                buttonId: 'Reset',
                btnClassName: 'secondary__btn',
                handleClick: (): Promise<void> => onResetButtonClick(true),
                isDisabled: false,
                buttonVariant: 'outlined',
            },
        ];
    }, [onResetButtonClick]);

    const handleFilterDataChange = useCallback(async (data, filterKey: string, isGlobal: boolean) => {
        const tableProps = cloneDeep(activeScreenTablePropsRef?.current);
        const res = await setFilterData(data, filterKey, activeFilterScreen, 'SET', isGlobal)(dispatch);
        if (res?.message === 'Action dispatched successfully') {
            await layoutCallback?.(
                1,
                tableProps?.pageSize,
                tableProps?.view,
                tableProps?.sortBy,
                tableProps?.order,
                res?.filters,
                tableProps?.searchTerm ?? '',
            )
        }
    }, [activeScreenTablePropsRef, activeFilterScreen, layoutCallback]);


    const onChangeOfSearchText = useCallback(async (searchText: string) => {
        const trimmedSearchText = searchText.trim();
        const tableProps = cloneDeep(activeScreenTablePropsRef?.current);
        if (tableProps) {
            tableProps.searchTerm = trimmedSearchText;
        }
        const activeScreenAppliedFilters = getCurrentFiltersValues(activeFilterScreen);
        await layoutCallback(
            1,
            tableProps?.pageSize,
            tableProps?.view,
            tableProps?.sortBy,
            tableProps?.order,
            activeScreenAppliedFilters,
            trimmedSearchText,
        )
    }, [activeScreenTablePropsRef, activeFilterScreen, layoutCallback]);

    const searchBoxConfiguration = useMemo(() => {
        return {
            searchBoxClassName: 'inventory__param__report__search__box',
            searchFieldId: `${navIdPrefix}-search-box`,
            searchFieldName: `${navIdPrefix}-search-box`,
            handleSearch: onChangeOfSearchText,
        };
    }, [navIdPrefix, onChangeOfSearchText]);

    const handleNullValuesForFields = (value : string)=> {
        if(!value)return '-';
        else return value;
    }

    const handleChargerClick = (rowData)=>{
        navigate('/charger?chargerId=' + rowData?.chargerVisibleId , {
            state:{
                 chargerId: rowData?.chargerId,
            }
        })
    }

    const inventoryParamLogsColumns = [
        {
            field: 'chargerVisibleId',
            headerName: 'Charger',
            flex: 1,
            sortable: true,
            minWidth: 150,
            renderCell: (params) => (
                <span
                    style={{
                        color: 'var(--secondary-blue-color)',
                        fontWeight: 400,
                        cursor:'pointer'
                    }}
                    onClick={()=>handleChargerClick(params.row)}
                >
                    {handleNullValuesForFields(params.value)}
                </span>
            )
        },
        {
            field: 'ocppId',
            headerName: 'OCPP Id',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'productType',
            headerName: 'Product Type',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'chargingStation',
            headerName: 'Charging Station',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'customer',
            headerName: 'Customer',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'component',
            headerName: 'Component',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'connector',
            headerName: 'Connector',
            flex: 1,
            minWidth: 120,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'smrUnitNo',
            headerName: 'SMR Unit No',
            flex: 1,
            minWidth: 120,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'serialNumber',
            headerName: 'Serial Number',
            flex: 1,
            minWidth: 120,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'hePartNumber',
            headerName: 'HE Part Number',
            flex: 1,
            minWidth: 120,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'swVersion',
            headerName: 'SW Version',
            flex: 1,
            minWidth: 120,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'macId',
            headerName: 'MAC Id',
            flex: 1,
            minWidth: 120,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'loggedAt',
            headerName: 'Logged At Time',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
               <span style={{ fontWeight: 400 }}>{formatRelativeTimeForTable(params.value)}</span>
            )
        },
    ]

    const inventoryParamChangeLogsColumnsList = [
        {
            field: 'chargerVisibleId',
            headerName: 'Charger',
            flex: 1,
            sortable: true,
            minWidth: 150,
            renderCell: (params) => (
                <span
                    style={{
                        color: 'var(--secondary-blue-color)',
                        fontWeight: 400,
                        cursor:'pointer'
                    }}
                    onClick={()=>handleChargerClick(params.row)}
                >
                    {handleNullValuesForFields(params.value)}
                </span>
            )
        },
        {
            field: 'ocppId',
            headerName: 'OCPP Id',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'productType',
            headerName: 'Product Type',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'chargingStation',
            headerName: 'Charging Station',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'customer',
            headerName: 'Customer',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'component',
            headerName: 'Component',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'connector',
            headerName: 'Connector',
            flex: 1,
            minWidth: 120,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'smrUnitNo',
            headerName: 'SMR Unit No',
            flex: 1,
            minWidth: 120,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'parameter',
            headerName: 'Parameter',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'oldValue',
            headerName: 'Old Value',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'newValue',
            headerName: 'New Value',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 400 }}>{handleNullValuesForFields(params.value)}</span>
            )
        },
        {
            field: 'changeType',
            headerName: 'Change Type',
            flex: 1,
            minWidth: 140,
            sortable: true,
            renderCell: (params) => (
                <InventoryParamChangeTypeBadge changeType={params.value} />
            )
        },
        {
            field: 'loggedAt',
            headerName: 'Changed At',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => (
               <span style={{ fontWeight: 400 }}>{formatRelativeTimeForTable(params.value)}</span>
            )
        }
    ]
    const inventoryParamFilterProps: FilterProp[] = useMemo(() => {

            const inventoryParamHistoryDateRangeValue = getFilterSelectedValue('inventoryParamHistoryDateRange', 'inventoryParamHistoryReport', false);
            const inventoryParamChangeLogCreatedOnDateRange = getFilterSelectedValue('changeLogLoggedAt', 'inventoryParamChangeLogsReport', false);
            const inventoryParamHistoryFilters: FilterProp[] = [
                {
                    filterLabel: 'Product Type',
                    filterId: 'productType',
                    filterType: 'dropdown',
                    filterDropdownProps: {
                        selectValue: getFilterSelectedValue('productType', 'inventoryParamHistoryReport', false),
                        selectOptions: chargerProductTypeReferenceData ?? [],
                        handleSelectOptionChange: (val): void => {
                            handleFilterDataChange(val, 'productType', false)
                        },
                        selectDropDownId: 'inventory-param-history-report-product-type-filter-dropdown',
                        isMultiSelect: true
                    }
                },
                {
                    filterLabel: 'Charger',
                    filterId: 'chargerId',
                    filterType: 'asyncDropdown',
                    asyncDropdownProps: {
                        fetchOptions: (search: string, offset: number, limit: number) =>
                            dispatch(fetchChargerVisibleIdsPaginatedList({ search, offset, limit })),
                        selected: getFilterSelectedValue('chargerId', 'inventoryParamHistoryReport', false),
                        onSelectedChange: (val): void => {
                            handleFilterDataChange(val, 'chargerId', false)
                        },
                        selectDropDownId: 'inventory-param-history-report-charger-filter-dropdown',
                        isMultiSelect: true
                    }
                },
                {
                    filterLabel: 'Component',
                    filterId: 'inventoryParamComponentType',
                    filterType: 'dropdown',
                    filterDropdownProps: {
                        selectValue: getFilterSelectedValue('inventoryParamComponentType', 'inventoryParamHistoryReport', false),
                        selectOptions: inventoryParamComponentTypeReferenceData ?? [],
                        handleSelectOptionChange: (val): void => {
                            handleFilterDataChange(val, 'inventoryParamComponentType', false)
                        },
                        selectDropDownId: 'inventory-param-history-report-component-filter-dropdown',
                        isMultiSelect: true
                    }
                },
                {
                    filterLabel: 'Interval',
                    filterId: 'inventoryParamHistoryDateRange',
                    filterType: 'dateFilter',
                    dateFilterProps: {
                        datePickerId: 'inventory-param-history-report-date-range-date-picker',
                        datePickerClassName: '',
                        selectsRange: true,
                        startDate: inventoryParamHistoryDateRangeValue?.[0]
                            ? new Date(inventoryParamHistoryDateRangeValue?.[0])
                            : null,
                        endDate: inventoryParamHistoryDateRangeValue?.[1]
                            ? new Date(inventoryParamHistoryDateRangeValue?.[1])
                            : null,
                        onChange: (val): void => {
                           handleFilterDataChange(val, 'inventoryParamHistoryDateRange', false);
                        },
                    }
                }
            ]
            const inventoryParamLatestLogsFilters: FilterProp[] = [
                {
                    filterLabel: 'Product Type',
                    filterId: 'productType',
                    filterType: 'dropdown',
                    filterDropdownProps: {
                        selectValue: getFilterSelectedValue('productType', 'inventoryParamLatestLogsReport', false),
                        selectOptions: chargerProductTypeReferenceData ?? [],
                        handleSelectOptionChange: (val): void => {
                            handleFilterDataChange(val, 'productType', false)
                        },
                        selectDropDownId: 'inventory-param-latest-logs-report-product-type-filter-dropdown',
                        isMultiSelect: true
                    }
                },
                {
                    filterLabel: 'Charger',
                    filterId: 'chargerId',
                    filterType: 'asyncDropdown',
                    asyncDropdownProps: {
                        fetchOptions: (search: string, offset: number, limit: number) =>
                            dispatch(fetchChargerVisibleIdsPaginatedList({ search, offset, limit })),
                        selected: getFilterSelectedValue('chargerId', 'inventoryParamLatestLogsReport', false),
                        onSelectedChange: (val): void => {
                            handleFilterDataChange(val, 'chargerId', false)
                        },
                        selectDropDownId: 'inventory-param-latest-logs-report-charger-filter-dropdown',
                        isMultiSelect: true
                    }
                },
                {
                    filterLabel: 'Component',
                    filterId: 'inventoryParamComponentType',
                    filterType: 'dropdown',
                    filterDropdownProps: {
                        selectValue: getFilterSelectedValue('inventoryParamComponentType', 'inventoryParamLatestLogsReport', false),
                        selectOptions: inventoryParamComponentTypeReferenceData ?? [],
                        handleSelectOptionChange: (val): void => {
                            handleFilterDataChange(val, 'inventoryParamComponentType', false)
                        },
                        selectDropDownId: 'inventory-param-latest-logs-report-component-filter-dropdown',
                        isMultiSelect: true
                    }
                }
            ]
            const inventoryParamChangeLogsFilters: FilterProp[] = [
                {
                    filterLabel: 'Product Type',
                    filterId: 'productType',
                    filterType: 'dropdown',
                    filterDropdownProps: {
                        selectValue: getFilterSelectedValue('productType', 'inventoryParamChangeLogsReport', false),
                        selectOptions: chargerProductTypeReferenceData ?? [],
                        handleSelectOptionChange: (val): void => {
                            handleFilterDataChange(val, 'productType', false)
                        },
                        selectDropDownId: 'inventory-param-change-logs-report-product-type-filter-dropdown',
                        isMultiSelect: true
                    }
                },
                {
                    filterLabel: 'Charger',
                    filterId: 'chargerId',
                    filterType: 'asyncDropdown',
                    asyncDropdownProps: {
                        fetchOptions: (search: string, offset: number, limit: number) =>
                            dispatch(fetchChargerVisibleIdsPaginatedList({ search, offset, limit })),
                        selected: getFilterSelectedValue('chargerId', 'inventoryParamChangeLogsReport', false),
                        onSelectedChange: (val): void => {
                            handleFilterDataChange(val, 'chargerId', false)
                        },
                        selectDropDownId: 'inventory-param-change-logs-report-charger-filter-dropdown',
                        isMultiSelect: true
                    }
                },
                {
                    filterLabel: 'Component',
                    filterId: 'inventoryParamComponentType',
                    filterType: 'dropdown',
                    filterDropdownProps: {
                        selectValue: getFilterSelectedValue('inventoryParamComponentType', 'inventoryParamChangeLogsReport', false),
                        selectOptions: inventoryParamComponentTypeReferenceData ?? [],
                        handleSelectOptionChange: (val): void => {
                            handleFilterDataChange(val, 'inventoryParamComponentType', false)
                        },
                        selectDropDownId: 'inventory-param-change-logs-report-component-filter-dropdown',
                        isMultiSelect: true
                    }
                },
                {
                    filterLabel: 'Parameter',
                    filterType: 'dropdown',
                    filterId: 'changeLogFieldName',
                    filterDropdownProps: {
                        selectValue: getFilterSelectedValue('changeLogFieldName', 'inventoryParamChangeLogsReport', false),
                        selectOptions: inventoryParamChangeLogFieldNameReferenceData ?? [],
                        handleSelectOptionChange: (val): void => {
                           handleFilterDataChange(val, 'changeLogFieldName', false)
                        },
                        selectDropDownId: 'inventory-param-change-logs-field-name-filter-dropdown',
                        isMultiSelect: true,
                        renderToBody:true,
                    }
                },
                {
                    filterLabel: 'Status',
                    filterType: 'dropdown',
                    filterId: 'changeLogChangeType',
                    filterDropdownProps: {
                        selectValue: getFilterSelectedValue('changeLogChangeType', 'inventoryParamChangeLogsReport', false),
                        selectOptions: inventoryParamChangeLogChangeTypeReferenceData ?? [],
                        handleSelectOptionChange: (val): void => {
                            handleFilterDataChange(val, 'changeLogChangeType', false)
                        },
                        selectDropDownId: 'inventory-param-change-logs-field-name-filter-dropdown',
                        isMultiSelect: true,
                        renderToBody:true,
                    }
                },
                {
                    filterLabel: 'Changed At',
                    filterId: 'changeLogLoggedAt',
                    filterType: 'dateFilter',
                    dateFilterProps: {
                        datePickerId: 'inventory-param-change-logs-changed-on-picker',
                        datePickerClassName: '',
                        selectsRange: true,
                        startDate: inventoryParamChangeLogCreatedOnDateRange?.[0]
                            ? new Date(inventoryParamChangeLogCreatedOnDateRange?.[0])
                            : null,
                        endDate: inventoryParamChangeLogCreatedOnDateRange?.[1]
                            ? new Date(inventoryParamChangeLogCreatedOnDateRange?.[1])
                            : null,
                        onChange: (val): void => {
                            handleFilterDataChange(val, 'changeLogLoggedAt', false);
                        },
                        renderToBody: true,
                    },
                }
            ]

            switch(selectedNav) {
                case 'latest':  return inventoryParamLatestLogsFilters;
                case 'history': return inventoryParamHistoryFilters;
                case 'changeLogs': return inventoryParamChangeLogsFilters; 
                default: return inventoryParamHistoryFilters;
            }
    },[chargerVisibleIdReferenceData, chargerProductTypeReferenceData, inventoryParamComponentTypeReferenceData, screenFilters, selectedNav, handleFilterDataChange]);

    const inventoryLogColumns = useMemo(()=>{
        if(selectedNav === 'latest') {
            if(!inventoryParamLatestLogsColumns) return inventoryParamLogsColumns;
            const applicableColumns = new Set(inventoryParamLatestLogsColumns);
            return inventoryParamLogsColumns.filter(column => applicableColumns.has(column.field));
        } else if (selectedNav === 'changeLogs') {
            if(!inventoryParamChangeLogsColumns) return inventoryParamChangeLogsColumnsList;
            const applicableColumns = new Set(inventoryParamChangeLogsColumns);
            return inventoryParamChangeLogsColumnsList.filter(column => applicableColumns.has(column.field));
        } else {
            if(!inventoryParamHistoryColumns) return inventoryParamLogsColumns;
            const applicableColumns = new Set(inventoryParamHistoryColumns);
            return inventoryParamLogsColumns.filter(column => applicableColumns.has(column.field));
        }
    },[selectedNav, inventoryParamChangeLogsColumnsList, inventoryParamLogsColumns, inventoryParamHistoryColumns, inventoryParamLatestLogsColumns, inventoryParamChangeLogsColumns]);

    const getTableRows = useCallback((): IInventoryParameterReport[] |  IInventoryParameterChangeLogReport[] => {
        switch(selectedNav) {
            case 'latest': return inventoryParamLatestLogsData !== null && inventoryParamLatestLogsData?.length > 0 ? inventoryParamLatestLogsData : [];
            case 'history': return inventoryParamHistoryData !== null && inventoryParamHistoryData?.length > 0 ? inventoryParamHistoryData : [];
            case 'changeLogs': return inventoryParamChangeLogsData !== null && inventoryParamChangeLogsData?.length > 0 ? inventoryParamChangeLogsData : []
            default: return inventoryParamHistoryData !== null && inventoryParamHistoryData?.length > 0 ? inventoryParamHistoryData : [];
        }
    }, [inventoryParamHistoryData, inventoryParamLatestLogsData, inventoryParamChangeLogsData, selectedNav]);


    const onExportBtnClick = useCallback(async (fileType: 'csv' | 'xlsx'): Promise<void> => {
        if (exportingFile) {
            // already exporting a file, do not allow another export
            return;
        }
        try {
            setExportingFile(true);
            const response = selectedNav === 'latest' ? await dispatch(exportInventoryParamReport({ fileType, filters: getCurrentFiltersValues('inventoryParamLatestLogsReport'), sortBy: 'loggedAt', order: 'descending' })) 
            : selectedNav === 'changeLogs' ? await dispatch(exportInventoryParamChangeLogReport({ fileType, filters: getCurrentFiltersValues('inventoryParamChangeLogsReport'), sortBy: 'loggedAt', order: 'descending' })) 
            : await dispatch(exportInventoryParamHistoryReport({ fileType, filters: getCurrentFiltersValues('inventoryParamHistoryReport'), sortBy: 'loggedAt', order: 'descending' }));

            if (response?.status === 200 || response?.status === 202) {
                downloadFile({ url: response?.data?.url });
                fileDownloadSuccessToast();
            } else {
                fileDownloadFailureToast();
                setExportAnchorEl(null);
            }
        } finally {
            setExportingFile(false);
            setExportAnchorEl(null);
        }
    }, [exportingFile, fileDownloadSuccessToast, fileDownloadFailureToast, selectedNav]);

    const showSkeleton = useMemo(()=> {
        switch(selectedNav) {
            case 'latest': return !inventoryParamLatestLogsData;
            case 'history': return !inventoryParamHistoryData;
            case 'changeLogs': return !inventoryParamChangeLogsData;
            default: return !inventoryParamHistoryData;
        }
    },[selectedNav,inventoryParamLatestLogsData,inventoryParamHistoryData,inventoryParamChangeLogsData]);

    const inventoryLogstableProps = useMemo(() => {
        switch(selectedNav) {
            case 'latest': return inventoryParamLatestLogsTableProps;
            case 'history': return inventoryParamHistoryTableProps;
            case 'changeLogs': return inventoryParamChangeLogsTableProps;
            default: return inventoryParamHistoryTableProps;
        }
    },[selectedNav,inventoryParamLatestLogsData,inventoryParamHistoryData,inventoryParamChangeLogsData])

    return (
         <>
            <TableDashboard
                layoutsEnabled={true}
                key={`${navIdPrefix}-table`}
                tableId={`${navIdPrefix}-table`}
                tableClassName="inventory__param__report__table"
                header='Inventory Report'
                searchBoxIncluded={true}
                searchBoxConfiguration={searchBoxConfiguration}
                gridColumns={inventoryLogColumns}
                tableRows={getTableRows()}
                layoutView='list'
                headerActionBtns={headerActionBtns}
                listLayoutCallBack={layoutCallback}
                totalCount={selectedNav === 'latest' ? inventoryParamLatestLogsDataCount : 0}
                showSkeleton={showSkeleton}
                totalCountText={'logs'}
                showLoader={inventoryParamHistoryLoader}
                pageNumber={inventoryLogstableProps?.pageNumber}
                tablePageSize={inventoryLogstableProps?.pageSize}
                sortField={inventoryLogstableProps?.sortBy}
                sortOrder={inventoryLogstableProps?.order}
                showRefreshButton={false}
                leftPinnedColumns={['chargerVisibleId']}
                navBarconfigurations={navBarconfigurations}
                enableCustomPagination = {selectedNav !== 'latest'}
            />

            <Menu
                id="export-option-dropdown"
                anchorEl={exportAnchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                className='export__option__dropdown'
                sx={{ top: '30px' }}
                open={Boolean(exportAnchorEl)}
                onClose={handleClose}
            >
                <MenuItem>
                    <div className='export__btn-option' onClick={(e) => { e?.stopPropagation(); onExportBtnClick('csv') }}>
                        To CSV
                    </div>
                </MenuItem>
                <MenuItem>

                    <div className='export__btn-option' onClick={(e) => {
                        e?.stopPropagation();
                        onExportBtnClick('xlsx')
                    }}>
                        To Excel
                    </div>
                </MenuItem>
            </Menu>

            {/* This is filter pop over */}
            <>
                {
                    filtersVisible && <SidePopover
                        key={`${navIdPrefix}-filters`}
                        id={`${navIdPrefix}-filters`}
                        open={filtersVisible}
                        containerClassName={'inventory-param-report-side-filters'}
                        handleClose={handleFiltersClosePopOver}
                        headerText={'Add Filters'}
                        // eslint-disable-next-line react/no-children-prop
                        children={<FilterLayout requiredReferenceDataCallbacks={['chargerVisibleId','productType','inventoryParamComponentType','changeLogFieldName','changeLogChangeType']}
                            filters={inventoryParamFilterProps} handleFilterDataChange={handleFilterDataChange}
                        />}
                        btnsList={filterFooterBtns}
                    />
                }
            </>
        </>
    )
}

export default InventoryParamReportDashboard